import { CAD_KIND_DEFAULT, CAD_KINDS } from '../data/cadConstants';
import { CAD_PLAN_METERS_PER_DEGREE, computeGeometryBbox } from './cadGeometry';

export const EMPTY_CAD_MASTERPLAN_COLLECTION = { type: 'FeatureCollection', features: [] };

const TREE_M2_PER_POINT = 240;
const TREE_MIN_PER_FEATURE = 8;
const TREE_MAX_PER_FEATURE = 160;
const TREE_ATTEMPTS_PER_POINT = 36;
const TREE_VARIANTS = 4;
/** Hilera en el límite del CAD (lote/calle vs satélite). No recubre la plaza. */
const EDGE_TREE_SPACING_M = 20;
const EDGE_TREE_OFFSET_M = 8;
const EDGE_TREE_PROBE_M = 14;
const EDGE_TREE_MIN_EDGE_M = 10;
const EDGE_TREE_MAX = 120;
/**
 * La copa es un sprite (~14 m de radio a zoom de chips con `lg`): un
 * centro fuera del lote igual lo tapa si el margen es chico. Antes 7 m
 * dejaba la copa encima de la manzana al pintar árboles sobre el fill.
 */
const TREE_LOT_CLEARANCE_M = 14;
const TREE_CLEARANCE_PROBES = 12;
/** Vértices de lotes vecinos casi coincidentes cuentan como la misma arista. */
const CURB_SNAP_DEG = 0.25 / CAD_PLAN_METERS_PER_DEGREE;

/**
 * `cad_kind` del contrato CAD. Vive en `attributes`; si falta, es un lote.
 *
 * @param {object|null|undefined} featureOrProps
 * @returns {'lot'|'street'|'green'|'site'}
 */
export function getCadKind(featureOrProps) {
  const props = featureOrProps?.properties && typeof featureOrProps.properties === 'object'
    ? featureOrProps.properties
    : (featureOrProps || {});
  const nested = props.attributes && typeof props.attributes === 'object'
    ? props.attributes.cad_kind
    : undefined;
  const raw = String(props.cad_kind || nested || '').trim().toLowerCase();
  return CAD_KINDS.includes(raw) ? raw : CAD_KIND_DEFAULT;
}

/**
 * Parcela vendible (ficha, hover, stock, chip). Calles/verdes/predio no.
 *
 * @param {object|null|undefined} featureOrProps
 * @returns {boolean}
 */
export function isCadSellableLot(featureOrProps) {
  return getCadKind(featureOrProps) === 'lot';
}

/**
 * @param {object[]} features
 * @returns {object[]}
 */
export function filterCadSellableLots(features) {
  const list = Array.isArray(features) ? features : [];
  return list.filter((feature) => isCadSellableLot(feature));
}

/**
 * Inventario: solo `lot`. Calle / verde / predio no son lotes.
 *
 * @param {object[]|null|undefined} features
 * @returns {number}
 */
export function countCadSellableLots(features) {
  return filterCadSellableLots(features).length;
}

export function cadKindEqualsFilter(kind) {
  return ['==', ['get', 'cad_kind'], kind];
}

/**
 * Bucket estable (`tone_seed`) para variar el pasto sin tocar la geometría.
 * Mismo id ⇒ mismo tono en cada render.
 *
 * @param {string|number|null|undefined} id
 * @param {number} buckets
 * @returns {number}
 */
export function cadFeatureToneSeed(id, buckets) {
  const n = Math.max(1, Math.floor(Number(buckets) || 1));
  return hashSeed(id) % n;
}

export const CAD_LOT_KIND_FILTER = cadKindEqualsFilter('lot');

function iterPolygons(geometry) {
  if (!geometry) return [];
  if (geometry.type === 'Polygon' && Array.isArray(geometry.coordinates)) {
    return [geometry.coordinates];
  }
  if (geometry.type === 'MultiPolygon' && Array.isArray(geometry.coordinates)) {
    return geometry.coordinates;
  }
  return [];
}

function isClosedRingVertex(ring, index) {
  if (!Array.isArray(ring) || ring.length < 2) return false;
  if (index !== ring.length - 1) return false;
  const first = ring[0];
  const last = ring[index];
  return first[0] === last[0] && first[1] === last[1];
}

function pointInRing(lng, lat, ring) {
  if (!Array.isArray(ring) || ring.length < 3) return false;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    if (isClosedRingVertex(ring, i)) continue;
    const xi = Number(ring[i]?.[0]);
    const yi = Number(ring[i]?.[1]);
    const xj = Number(ring[j]?.[0]);
    const yj = Number(ring[j]?.[1]);
    if (![xi, yi, xj, yj].every(Number.isFinite)) continue;
    const crosses = ((yi > lat) !== (yj > lat))
      && (lng < ((xj - xi) * (lat - yi)) / ((yj - yi) || Number.EPSILON) + xi);
    if (crosses) inside = !inside;
  }
  return inside;
}

function pointInPolygon(lng, lat, polygon) {
  const exterior = polygon?.[0];
  if (!pointInRing(lng, lat, exterior)) return false;
  for (let i = 1; i < polygon.length; i += 1) {
    if (pointInRing(lng, lat, polygon[i])) return false;
  }
  return true;
}

function pointInGeometry(lng, lat, geometry) {
  return iterPolygons(geometry).some((polygon) => pointInPolygon(lng, lat, polygon));
}

function hashSeed(value) {
  const raw = String(value || '');
  let hash = 2166136261;
  for (let i = 0; i < raw.length; i += 1) {
    hash ^= raw.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function estimateAreaM2(geometry, areaM2) {
  const given = Number(areaM2);
  if (Number.isFinite(given) && given > 0) return given;
  const bbox = computeGeometryBbox(geometry);
  if (!bbox) return 0;
  const midLat = (bbox[1] + bbox[3]) / 2;
  const cosLat = Math.cos((midLat * Math.PI) / 180);
  const widthM = (bbox[2] - bbox[0]) * CAD_PLAN_METERS_PER_DEGREE * Math.max(0.2, Math.abs(cosLat) || 0.2);
  const heightM = (bbox[3] - bbox[1]) * CAD_PLAN_METERS_PER_DEGREE;
  return Math.abs(widthM * heightM) * 0.55;
}

function treeCountForArea(areaM2) {
  if (!(areaM2 > 0)) return 0;
  const n = Math.round(areaM2 / TREE_M2_PER_POINT);
  return Math.max(TREE_MIN_PER_FEATURE, Math.min(TREE_MAX_PER_FEATURE, n));
}

/**
 * Anillos interiores de `street`: cada uno es el borde de una manzana.
 * @param {object[]|null|undefined} features
 * @param {(ring: number[][], key: string) => void} visit
 */
function forEachCadBlockRing(features, visit) {
  (Array.isArray(features) ? features : []).forEach((feature) => {
    if (getCadKind(feature) !== 'street') return;
    iterPolygons(feature?.geometry).forEach((polygon, polygonIndex) => {
      (polygon || []).slice(1).forEach((ring, ringIndex) => {
        if (!Array.isArray(ring) || ring.length < 4) return;
        visit(ring, `${feature.id || 'street'}-${polygonIndex}-${ringIndex}`);
      });
    });
  });
}

function openRing(ring) {
  const points = (Array.isArray(ring) ? ring : []).filter((point) => Array.isArray(point)
    && Number.isFinite(Number(point[0]))
    && Number.isFinite(Number(point[1])));
  if (points.length < 2) return points;
  const first = points[0];
  const last = points[points.length - 1];
  if (first[0] === last[0] && first[1] === last[1]) return points.slice(0, -1);
  return points;
}

function closeRing(points) {
  const open = openRing(points);
  if (open.length < 3) return null;
  return [...open, [open[0][0], open[0][1]]];
}

function snapCoord(value) {
  return Math.round(Number(value) / CURB_SNAP_DEG) * CURB_SNAP_DEG;
}

function pointKey(point) {
  return `${snapCoord(point[0])},${snapCoord(point[1])}`;
}

function edgeKey(a, b) {
  const ka = pointKey(a);
  const kb = pointKey(b);
  return ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
}

function isCadCurbPad(feature) {
  const kind = getCadKind(feature);
  return kind === 'lot' || kind === 'green';
}

function isCadFootprintKind(kind) {
  return kind === 'lot' || kind === 'street' || kind === 'green';
}

function pointInCadLot(lng, lat, features) {
  return (Array.isArray(features) ? features : []).some((feature) => (
    getCadKind(feature) === 'lot' && pointInGeometry(lng, lat, feature.geometry)
  ));
}

function pointInCadFootprint(lng, lat, features) {
  return (Array.isArray(features) ? features : []).some((feature) => (
    isCadFootprintKind(getCadKind(feature))
    && pointInGeometry(lng, lat, feature.geometry)
  ));
}

/** El punto y su copa quedan fuera de todo lote. */
function treeClearOfCadLots(lng, lat, features, cosLat) {
  if (pointInCadLot(lng, lat, features)) return false;
  const dist = TREE_LOT_CLEARANCE_M / CAD_PLAN_METERS_PER_DEGREE;
  for (let i = 0; i < TREE_CLEARANCE_PROBES; i += 1) {
    const angle = (i / TREE_CLEARANCE_PROBES) * Math.PI * 2;
    const probeLng = lng + ((Math.cos(angle) * dist) / cosLat);
    const probeLat = lat + (Math.sin(angle) * dist);
    if (pointInCadLot(probeLng, probeLat, features)) return false;
  }
  return true;
}

function edgeLengthMeters(a, b, cosLat) {
  const dx = (Number(b[0]) - Number(a[0])) * CAD_PLAN_METERS_PER_DEGREE * cosLat;
  const dy = (Number(b[1]) - Number(a[1])) * CAD_PLAN_METERS_PER_DEGREE;
  return Math.hypot(dx, dy);
}

/**
 * Aristas de calle (incluye huecos de la red vial).
 * @param {object[]|null|undefined} features
 * @param {(a: number[], b: number[]) => void} visit
 */
function forEachStreetEdge(features, visit) {
  (Array.isArray(features) ? features : []).forEach((feature) => {
    if (getCadKind(feature) !== 'street') return;
    iterPolygons(feature?.geometry).forEach((polygon) => {
      (polygon || []).forEach((ring) => {
        const points = openRing(ring);
        const total = points.length;
        if (total < 3) return;
        for (let i = 0; i < total; i += 1) {
          visit(points[i], points[(i + 1) % total]);
        }
      });
    });
  });
}

function isSatelliteFacingEdge(a, b, cosLat, features) {
  const plus = offsetAlongEdge(a, b, 0.5, EDGE_TREE_PROBE_M, 1, cosLat);
  const minus = offsetAlongEdge(a, b, 0.5, EDGE_TREE_PROBE_M, -1, cosLat);
  const plusIn = Boolean(plus && pointInCadFootprint(plus[0], plus[1], features));
  const minusIn = Boolean(minus && pointInCadFootprint(minus[0], minus[1], features));
  return plusIn !== minusIn;
}

/**
 * Solo el lado que da al satélite: un probe cae en el CAD y el otro no.
 * Una calle interna tiene CAD a ambos lados (lotes o la propia calzada).
 * @param {object[]} features
 * @param {number} cosLat
 * @returns {{ a: number[], b: number[] }[]}
 */
function satelliteFacingStreetEdges(features, cosLat) {
  const seen = new Set();
  const edges = [];
  forEachStreetEdge(features, (a, b) => {
    const key = edgeKey(a, b);
    if (seen.has(key)) return;
    seen.add(key);
    if (!isSatelliteFacingEdge(a, b, cosLat, features)) return;
    edges.push({ a, b });
  });
  return edges;
}

function offsetAlongEdge(a, b, t, meters, sign, cosLat) {
  const lng = Number(a[0]) + ((Number(b[0]) - Number(a[0])) * t);
  const lat = Number(a[1]) + ((Number(b[1]) - Number(a[1])) * t);
  const dx = (Number(b[0]) - Number(a[0])) * cosLat;
  const dy = Number(b[1]) - Number(a[1]);
  const len = Math.hypot(dx, dy);
  if (!(len > 1e-12) || !(meters > 0)) return null;
  const dist = meters / CAD_PLAN_METERS_PER_DEGREE;
  const nx = ((-dy) / len) * sign;
  const ny = (dx / len) * sign;
  return [lng + ((nx * dist) / cosLat), lat + (ny * dist)];
}

function outwardEdgePoint(a, b, t, meters, cosLat, features) {
  const plus = offsetAlongEdge(a, b, t, meters, 1, cosLat);
  const minus = offsetAlongEdge(a, b, t, meters, -1, cosLat);
  if (plus && !pointInCadFootprint(plus[0], plus[1], features)) return plus;
  if (minus && !pointInCadFootprint(minus[0], minus[1], features)) return minus;
  return null;
}

function makeTreePoint(id, lng, lat, rand) {
  const size = rand();
  return {
    type: 'Feature',
    id,
    properties: {
      cad_kind: 'green',
      tree_role: String(id).startsWith('edge-tree') ? 'edge' : 'green',
      tree_size: size < 0.22 ? 'sm' : (size > 0.72 ? 'lg' : 'md'),
      tree_variant: Math.floor(rand() * TREE_VARIANTS) % TREE_VARIANTS,
    },
    geometry: { type: 'Point', coordinates: [lng, lat] },
  };
}

/**
 * Hilera suelta del lado del satélite. Determinista. Tope para no tapar el loteo.
 * @param {object[]|null|undefined} features
 * @returns {object[]}
 */
function buildCadEdgeTreePoints(features) {
  const list = Array.isArray(features) ? features : [];
  const sample = list.find((feature) => feature?.geometry);
  const bbox = sample ? computeGeometryBbox(sample.geometry) : null;
  const midLat = bbox ? (bbox[1] + bbox[3]) / 2 : 0;
  const cosLat = Math.max(0.2, Math.abs(Math.cos((midLat * Math.PI) / 180)) || 1);
  const edges = satelliteFacingStreetEdges(list, cosLat);
  if (!edges.length) return [];
  const rand = mulberry32(hashSeed('cad-edge-trees'));
  const points = [];
  edges.forEach((edge) => {
    if (points.length >= EDGE_TREE_MAX) return;
    const lengthM = edgeLengthMeters(edge.a, edge.b, cosLat);
    if (!(lengthM >= EDGE_TREE_MIN_EDGE_M)) return;
    const count = Math.max(1, Math.round(lengthM / EDGE_TREE_SPACING_M));
    for (let i = 0; i < count; i += 1) {
      if (points.length >= EDGE_TREE_MAX) break;
      const jitter = (rand() - 0.5) * 0.22;
      const t = Math.min(0.92, Math.max(0.08, ((i + 0.5) / count) + jitter));
      const offsetM = EDGE_TREE_OFFSET_M + ((rand() - 0.5) * 1.6);
      const point = outwardEdgePoint(edge.a, edge.b, t, offsetM, cosLat, list);
      if (!point || !treeClearOfCadLots(point[0], point[1], list, cosLat)) continue;
      points.push(makeTreePoint(`edge-tree-${points.length}`, point[0], point[1], rand));
    }
  });
  return points;
}

function forEachCurbPadRing(features, visit) {
  (Array.isArray(features) ? features : []).forEach((feature) => {
    if (!isCadCurbPad(feature)) return;
    iterPolygons(feature?.geometry).forEach((polygon) => {
      const ring = polygon?.[0];
      if (!Array.isArray(ring) || ring.length < 4) return;
      visit(ring);
    });
  });
}

/**
 * Aristas de lote o verde que no se comparten: el perímetro contra la calle
 * (y el satélite). Un plaza queda con cordón; el borde lote-verde no.
 * @param {object[]|null|undefined} features
 * @returns {{ a: number[], b: number[] }[]}
 */
function uniqueCurbPadEdges(features) {
  const counts = new Map();
  forEachCurbPadRing(features, (ring) => {
    const points = openRing(ring);
    const total = points.length;
    if (total < 3) return;
    for (let i = 0; i < total; i += 1) {
      const a = points[i];
      const b = points[(i + 1) % total];
      const key = edgeKey(a, b);
      const prev = counts.get(key);
      if (prev) prev.count += 1;
      else counts.set(key, { a, b, count: 1 });
    }
  });
  return [...counts.values()]
    .filter((edge) => edge.count === 1)
    .map((edge) => ({ a: edge.a, b: edge.b }));
}

/**
 * Encadena aristas únicas en anillos cerrados. Un LineString abierto deja la
 * junta del primer/último vértice sin `line-join` (la punta que no cierra).
 * @param {{ a: number[], b: number[] }[]} edges
 * @returns {number[][][]}
 */
function ringsFromUniqueEdges(edges) {
  const adj = new Map();
  edges.forEach((edge, index) => {
    const ka = pointKey(edge.a);
    const kb = pointKey(edge.b);
    if (!adj.has(ka)) adj.set(ka, []);
    if (!adj.has(kb)) adj.set(kb, []);
    adj.get(ka).push({ to: edge.b, toKey: kb, index });
    adj.get(kb).push({ to: edge.a, toKey: ka, index });
  });

  const used = new Set();
  const rings = [];

  const walk = (startKey, startPoint) => {
    const ring = [[startPoint[0], startPoint[1]]];
    let currentKey = startKey;
    let guard = 0;
    while (guard < edges.length + 2) {
      guard += 1;
      const next = (adj.get(currentKey) || []).find((item) => !used.has(item.index));
      if (!next) break;
      used.add(next.index);
      if (next.toKey === startKey && ring.length >= 3) {
        ring.push([startPoint[0], startPoint[1]]);
        return ring;
      }
      ring.push([next.to[0], next.to[1]]);
      currentKey = next.toKey;
    }
    return closeRing(ring);
  };

  for (let i = 0; i < edges.length; i += 1) {
    if (used.has(i)) continue;
    const closed = walk(pointKey(edges[i].a), edges[i].a);
    if (closed && closed.length >= 4) rings.push(closed);
  }
  return rings;
}

function ringSignedArea(points) {
  const open = openRing(points);
  let sum = 0;
  for (let i = 0, j = open.length - 1; i < open.length; j = i, i += 1) {
    sum += (open[j][0] * open[i][1]) - (open[i][0] * open[j][1]);
  }
  return sum / 2;
}

function asCcwClosed(ring) {
  const closed = closeRing(ring);
  if (!closed) return null;
  if (ringSignedArea(closed) >= 0) return closed;
  return closeRing(openRing(closed).reverse());
}

function polygonCurbFeature(id, ring) {
  const closed = asCcwClosed(ring);
  if (!closed) return null;
  return {
    type: 'Feature',
    id,
    properties: { cad_kind: 'street' },
    geometry: { type: 'Polygon', coordinates: [closed] },
  };
}

/**
 * Perímetro para el cordón: bordes de lote y verde que no se tocan entre sí.
 * Si no hay ninguno, los huecos de `street`. `Polygon` para que MapLibre
 * cierre el join.
 *
 * @param {object[]|null|undefined} features
 * @returns {GeoJSON.FeatureCollection}
 */
export function buildCadStreetCurbCollection(features) {
  const fromPads = ringsFromUniqueEdges(uniqueCurbPadEdges(features))
    .map((ring, index) => polygonCurbFeature(`pad-block-${index}`, ring))
    .filter(Boolean);
  if (fromPads.length) return { type: 'FeatureCollection', features: fromPads };

  const fromStreets = [];
  forEachCadBlockRing(features, (ring, key) => {
    const feature = polygonCurbFeature(`${key}-curb`, ring);
    if (feature) fromStreets.push(feature);
  });
  return { type: 'FeatureCollection', features: fromStreets };
}

/**
 * Vegetación de cliente: copas adentro de cada `green`, más una hilera en
 * la calle del perímetro que da al satélite. No toca el DXF.
 *
 * Ninguna copa puede caer sobre un lote (centro ni radio de sprite): el
 * fill de manzana es stock vendible. Un plaza angosto junto al lote puede
 * quedar con menos árboles; mejor eso que tapar la parcela.
 *
 * @param {object[]|null|undefined} features
 * @returns {GeoJSON.FeatureCollection}
 */
export function buildCadGreenTreeCollection(features) {
  const list = Array.isArray(features) ? features : [];
  const sample = list.find((feature) => feature?.geometry);
  const bboxSample = sample ? computeGeometryBbox(sample.geometry) : null;
  const midLat = bboxSample ? (bboxSample[1] + bboxSample[3]) / 2 : 0;
  const cosLat = Math.max(0.2, Math.abs(Math.cos((midLat * Math.PI) / 180)) || 1);
  const points = [];
  list.forEach((feature) => {
    if (getCadKind(feature) !== 'green') return;
    const geometry = feature?.geometry;
    const bbox = computeGeometryBbox(geometry);
    if (!bbox) return;
    const areaM2 = estimateAreaM2(geometry, feature?.properties?.area_m2);
    const count = treeCountForArea(areaM2);
    if (count <= 0) return;
    const rand = mulberry32(hashSeed(feature.id || feature.properties?.id || 'green'));
    const [minLng, minLat, maxLng, maxLat] = bbox;
    const spanLng = maxLng - minLng;
    const spanLat = maxLat - minLat;
    if (!(spanLng > 0) || !(spanLat > 0)) return;
    let placed = 0;
    let attempts = 0;
    const maxAttempts = count * TREE_ATTEMPTS_PER_POINT;
    while (placed < count && attempts < maxAttempts) {
      attempts += 1;
      const lng = minLng + rand() * spanLng;
      const lat = minLat + rand() * spanLat;
      if (!pointInGeometry(lng, lat, geometry)) continue;
      // Centro y radio de copa fuera de todo lote (plaza que pisa manzana).
      if (!treeClearOfCadLots(lng, lat, list, cosLat)) continue;
      points.push(makeTreePoint(`${feature.id || 'green'}-tree-${placed}`, lng, lat, rand));
      placed += 1;
    }
  });
  const all = points.concat(buildCadEdgeTreePoints(list))
    .filter((item) => {
      const [lng, lat] = item.geometry.coordinates;
      return treeClearOfCadLots(lng, lat, list, cosLat);
    });
  return {
    type: 'FeatureCollection',
    features: all,
  };
}
